import React, { useState, useEffect, useRef } from "react";
import {
  Navbar,
  Button,
  IconButton,
  Collapse,
  Typography,
} from "@material-tailwind/react";
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "./AuthContext";
import { useTheme } from "./ThemeContext";
import { listMessages } from "./api/organizationMessagesService";
import { getPlatformIssuerStatus, getWallet } from "./api/promoCreditsService";
import { prospectAccess } from "./api/prospectService";
// Notifications temporarily disabled: restore the import and mounts to re-enable API calls.
// import NotificationBell from "./components/NotificationBell";
import BrandGuidelineReminder from "./components/BrandGuidelineReminder";
import { MegaMenuWithHover } from "./pages/MegaMenuWithHover";
import { logoutAzure } from "./authConfig";
import {
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  TvIcon,
  FolderIcon,
  CalendarIcon,
  MegaphoneIcon,
  CheckBadgeIcon,
  UserPlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/solid";

// Both public and authenticated states use this shell so the product retains
// one consistent navigation bar without exposing authenticated controls early.
const NAVBAR_SHELL_CLASS =
  "sticky top-0 z-20 h-max max-w-full rounded-none border-0 bg-iconBlue px-4 py-2 lg:px-8 lg:py-4";

// Keep the guest conversion navigation visually aligned with the authenticated
// workspace shell without coupling it to the workspace-only icon menu.
const GUEST_NAV_LINK_BASE_CLASS =
  "min-h-11 shrink-0 whitespace-nowrap rounded-md px-2 py-2 text-xs font-bold leading-tight text-white transition-colors hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/80 sm:px-3 wide:text-sm";
const GUEST_NAV_LINK_CLASS = `${GUEST_NAV_LINK_BASE_CLASS} text-center`;
const GUEST_MOBILE_NAV_LINK_CLASS = `${GUEST_NAV_LINK_BASE_CLASS} text-left`;

export function StickyOrgNavbar({ forceGuestNavbar = false }) {
  const {
    isLoggedIn,
    authReady,
    isApproverLoggedIn,
    activeWorkspace, 
    setIsApproverLoggedIn,
    setIsLoggedIn,
    getStepCompletedById,
    isFree7DaysTrial,
    daysRemainingFreeTrial,
    user_type,
    subscription_id,
    setUser_type,
    logout,
  } = useAuth();

  const { theme, toggleTheme } = useTheme();
  const { instance } = useMsal();
  const location = useLocation();
  const [activeNavItem, setActiveNavItem] = useState(location.pathname);
  const [openNav, setOpenNav] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [canManageCredits, setCanManageCredits] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(null);
  const [canUseProspects, setCanUseProspects] = useState(false);
  const menuButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  // The GTM guest journey continues into the organization auth screen. Keep
  // its conversion actions visible there instead of replacing them just as a
  // visitor opens Sign in or Signup.
  const isGtmGuestJourney = ["/", "/gtm-blueprint", "/create-your-gtm-blueprint", "/watch-demo", "/pricing", "/admin/upgrade", "/orgpasswordlogin", "/signup", "/become-consultant", "/privacy-policy", "/meta-data-deletion", "/terms-of-service"].includes(
    location.pathname
  );

  useEffect(() => {
    setActiveNavItem(location.pathname);
    setOpenNav(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!authReady || !isLoggedIn || !subscription_id || forceGuestNavbar) { setUnreadMessages(0); return undefined; }
    let active = true;
    let interval;
    const load = () => listMessages(subscription_id).then(data => {
      if (active) setUnreadMessages(data.unread || 0);
    }).catch((error) => {
      if (active && [400, 401, 403].includes(error?.response?.status)) {
        setUnreadMessages(0);
        clearInterval(interval);
      }
    });
    load();
    interval = setInterval(load, 60000);
    return () => { active = false; clearInterval(interval); };
  }, [authReady, isLoggedIn, subscription_id, forceGuestNavbar, location.pathname]);

  useEffect(() => {
    setCanUseProspects(false);
    if (!authReady || !isLoggedIn || !subscription_id || forceGuestNavbar) return undefined;
    let active = true;
    prospectAccess(subscription_id).then(() => { if (active) setCanUseProspects(true); }).catch(() => {});
    return () => { active = false; };
  }, [authReady, isLoggedIn, subscription_id, forceGuestNavbar]);

  useEffect(() => {
    setCanManageCredits(false);
    if (!authReady || !isLoggedIn || user_type !== "admin" || !subscription_id || forceGuestNavbar) return undefined;
    let active = true;
    getPlatformIssuerStatus(subscription_id)
      .then((status) => { if (active) setCanManageCredits(Boolean(status.can_manage_credits)); })
      .catch(() => { if (active) setCanManageCredits(false); });
    return () => { active = false; };
  }, [authReady, isLoggedIn, user_type, subscription_id, forceGuestNavbar]);

  useEffect(() => {
    setAvailableCredits(null);
    if (!authReady || !isLoggedIn || user_type !== "admin" || !subscription_id || forceGuestNavbar) return undefined;
    let active = true;
    const refreshCredits = () => getWallet(subscription_id)
      .then((wallet) => { if (active) setAvailableCredits(wallet.available); })
      .catch(() => { if (active) setAvailableCredits(null); });
    refreshCredits();
    window.addEventListener("credits-updated", refreshCredits);
    return () => { active = false; window.removeEventListener("credits-updated", refreshCredits); };
  }, [authReady, isLoggedIn, user_type, subscription_id, forceGuestNavbar, location.pathname]);

  useEffect(() => {
    if (!openNav) {
      return undefined;
    }

    const firstMenuControl = mobileMenuRef.current?.querySelector(
      'a, button:not([disabled])'
    );
    firstMenuControl?.focus();

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeMobileMenu();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [openNav]);

  function closeMobileMenu() {
    setOpenNav(false);
    menuButtonRef.current?.focus();
  }

  function handleMobileMenuKeyDown(event) {
    if (event.key !== "Tab") return;

    const focusableItems = mobileMenuRef.current?.querySelectorAll(
      'a, button:not([disabled])'
    );
    if (!focusableItems?.length) return;

    const firstItem = focusableItems[0];
    const lastItem = focusableItems[focusableItems.length - 1];

    if (event.shiftKey && document.activeElement === firstItem) {
      event.preventDefault();
      lastItem.focus();
    } else if (!event.shiftKey && document.activeElement === lastItem) {
      event.preventDefault();
      firstItem.focus();
    }
  }

  async function handleClickLogout() {
    await logout();
    await logoutAzure(instance, setIsLoggedIn, setIsApproverLoggedIn, setUser_type);
  }

  // Dashboard is the active approver workspace item. From the admin workspace,
  // the Organization menu provides the explicit "Approver Dashboard" switch.
  const navItems = [
    ...(isApproverLoggedIn && activeWorkspace === "approver"
      ? [
          {
            route: "/dashboard",
            icon: <CheckBadgeIcon className="h-5 w-5 mr-1" />,
            label: "Dashboard",
            stepId: -1,
          },
        ]
      : []),
    {
      route: "/businessunits",
      icon: <BuildingOfficeIcon className="h-5 w-5 mr-1" />,
      label: `Business Units`,
      stepId: 0,
    },
    {
      route: "/offerings",
      icon: <BuildingStorefrontIcon className="h-5 w-5 mr-1" />,
      label: "Offerings",
      stepId: 1,
    },
    {
      route: "/marketing",
      icon: <TvIcon className="h-5 w-5 mr-1" />,
      label: "Marketing Assets",
      stepId: 2,
    },
    {
      route: "/content",
      icon: <FolderIcon className="h-5 w-5 mr-1" />,
      label: "Content",
      stepId: 3,
    },
    {
      route: "/campaign",
      icon: <CalendarIcon className="h-5 w-5 mr-1" />,
      label: "Campaign",
      stepId: 4,
    },
    {
      route: "/create-your-gtm-blueprint",
      icon: <MegaphoneIcon className="h-5 w-5 mr-1" />,
      label: "Create Free GTM Blueprints",
      stepId: -4,
    },
    {
      route: "/marketingintegrations",
      icon: <RocketLaunchIcon className="h-5 w-5 mr-1" />,
      label: "Marketing Integrations",
      stepId: -4, // was -2
    },
    {
      route: "/become-consultant",
      icon: <UserPlusIcon className="h-5 w-5 mr-1" />,
      label: "Become a StunM Consultant",
      stepId: -5,
    },
  ];

  // const renderNavItem = (item) => (
  //   <li key={item.route} className="flex items-center justify-center">
  //     <Link to={item.route} onClick={() => setActiveNavItem(item.route)}>
  //       <Button
  //         fullWidth
  //         size="sm"
  //         variant={
  //           isLoggedIn && getStepCompletedById(item.stepId) ? "filled" : "text"
  //         }
  //         className={`flex capitalize items-center ${
  //           activeNavItem === item.route
  //             ? "text-white bg-darkPurple hover:bg-lightPurple"
  //             : isLoggedIn && getStepCompletedById(item.stepId)
  //             ? "text-white bg-white/20 hover:bg-white/30"
  //             : "text-white/70"
  //         }`}
  //       >
  //         {item.icon}
  //         {item.label}
  //       </Button>
  //     </Link>
  //   </li>
  // );

  const renderNavItem = (item, showLabel = false) => {
    const isActive = activeNavItem === item.route;
    const isCompleted = isLoggedIn && getStepCompletedById(item.stepId);

    return (
      <li
        key={item.route}
        className={`flex items-center justify-center ${showLabel ? "w-full" : ""}`}
      >
        <Link
          to={item.route}
          aria-current={isActive ? "page" : undefined}
          onClick={() => {
            setActiveNavItem(item.route);
            setOpenNav(false);
          }}
          className={`relative align-middle select-none font-sans font-bold text-center transition-all rounded-lg hover:bg-gray-900/10 active:bg-gray-900/20 focus:outline-none focus:ring-2 focus:ring-white/80 flex capitalize items-center px-2 py-1.5 text-xs wide:text-sm whitespace-nowrap ${
            showLabel ? "w-full justify-start" : ""
          } ${
            isActive
              ? "text-white bg-darkPurple hover:bg-lightPurple"
              : isCompleted
              ? "text-white bg-white/20 hover:bg-white/30"
              : "text-white/70"
          }`}
        >
          {React.cloneElement(item.icon, { className: "h-4 w-4 wide:mr-1" })}
          <span className={showLabel ? "inline" : "hidden xl:inline"}>
            {item.label}
          </span>
        </Link>
      </li>
    );
  };
  const LogoutButton = ({ fullWidthOnMobile = false }) => (
    <Button
      onClick={handleClickLogout}
      variant="text"
      size="sm"
      className={`flex-shrink-0 flex capitalize items-center text-white ${
        fullWidthOnMobile ? "w-full" : ""
      }`}
    >
      {isLoggedIn ? (
        <>
          <ArrowUpIcon className="h-5 w-5 mr-1" /> Log out
        </>
      ) : (
        <>
          <ArrowDownIcon className="h-5 w-5 mr-1" /> Login
        </>
      )}
    </Button>
  );

  const compactNavItems = navItems.slice(0, 3);

  // Workflow destinations only become useful after the current user's role
  // and permissions are available. Keep the public sign-in experience focused.
  if (forceGuestNavbar || !isLoggedIn) {
    return (
      <>
        <Navbar
          blurred={false}
          className={NAVBAR_SHELL_CLASS}
        >
          <div className="flex items-center justify-between text-white lg:hidden">
            <a
              href="https://stunm.in"
              className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
              aria-label="Visit StunM home"
            >
              <img
                src="/logo512.png"
                alt="StunM"
                className="h-14 w-auto sm:h-14 wide:h-20"
              />
            </a>
            <IconButton
              variant="text"
              className="!h-11 !w-11 !max-h-none !max-w-none text-white"
              ref={menuButtonRef}
              aria-label="Toggle menu"
              aria-expanded={openNav}
              aria-controls="guest-mobile-menu"
              onClick={() => setOpenNav((isOpen) => !isOpen)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </IconButton>
          </div>
          <div className="hidden lg:flex lg:items-center lg:gap-4 xl:gap-6 text-white">
            <a href="https://stunm.in" className="flex shrink-0 items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80" aria-label="Visit StunM home">
              <img src="/logo512.png" alt="StunM" className="h-14 w-auto wide:h-20" />
            </a>
            <Link to="/signup?tab=signup" className={GUEST_NAV_LINK_CLASS}>Start free blueprint</Link>
            <Link to="/become-consultant" className={GUEST_NAV_LINK_CLASS}>Become a StunM Consultant</Link>
            {isGtmGuestJourney && (
              <>
                <Link to="/watch-demo" className={GUEST_NAV_LINK_CLASS}>Watch demo</Link>
                <Link to="/pricing" className={GUEST_NAV_LINK_CLASS}>Pricing</Link>
                <Link to="/signup?tab=signup" className={GUEST_NAV_LINK_CLASS}>Plan and execute your campaigns</Link>
              </>
            )}
            <Link to="/signup?tab=login" className={`ml-auto px-3 ${GUEST_NAV_LINK_CLASS}`}>Sign in</Link>
          </div>
          <Collapse open={openNav}>
            <div id="guest-mobile-menu" ref={mobileMenuRef} onKeyDown={handleMobileMenuKeyDown} className="flex flex-col gap-2 pt-2 text-white lg:hidden">
              <div className="flex justify-end">
                <IconButton variant="text" className="!h-11 !w-11 !max-h-none !max-w-none text-white" aria-label="Close menu" onClick={closeMobileMenu}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </IconButton>
              </div>
              <Link to="/signup?tab=signup" className={`${GUEST_MOBILE_NAV_LINK_CLASS} w-full`}>Start free blueprint</Link>
              <Link to="/become-consultant" className={`${GUEST_MOBILE_NAV_LINK_CLASS} w-full`}>Become a StunM Consultant</Link>
              {isGtmGuestJourney && (
                <>
                  <Link to="/watch-demo" className={`${GUEST_MOBILE_NAV_LINK_CLASS} w-full`}>Watch demo</Link>
                  <Link to="/pricing" className={`${GUEST_MOBILE_NAV_LINK_CLASS} w-full`}>Pricing</Link>
                  <Link to="/signup?tab=signup" className={`${GUEST_MOBILE_NAV_LINK_CLASS} w-full`}>Plan and execute your campaigns</Link>
                </>
              )}
              <Link to="/signup?tab=login" className={`${GUEST_MOBILE_NAV_LINK_CLASS} w-full`}>Sign in</Link>
            </div>
          </Collapse>
        </Navbar>

      </>
    );
  }

  return (
    <div>
      <Navbar
        blurred={false}
        className={NAVBAR_SHELL_CLASS}
      >
        <div className="flex items-center gap-6 text-white">
          <Typography
            as="a"
            href="/home/organization"
            onClick={(e) => {
              if (!isLoggedIn) {
                e.preventDefault();
                window.location.href = "https://stunm.in";
              }
            }}
            className="cursor-pointer py-1.5 font-bold flex items-center gap-1 text-white shrink-0"
          >
            <img
              src="/logo512.png"
              alt="StunM"
              className="h-14 w-auto sm:h-14 wide:h-20 shrink-0"
            />
          </Typography>

          {/* Compact shortcuts are reserved for tablet-width layouts. */}
          <ul className="hidden lg:flex xl:hidden items-center gap-1">
            {compactNavItems.map((item) => (
              <li key={item.route}>
                <Link
                  to={item.route}
                  aria-label={item.label}
                  title={item.label}
                  aria-current={activeNavItem === item.route ? "page" : undefined}
                  onClick={() => setActiveNavItem(item.route)}
                >
                  <IconButton
                    variant="text"
                    className={`!h-11 !w-11 !max-h-none !max-w-none text-white ${
                      activeNavItem === item.route ? "bg-white/20" : ""
                    }`}
                  >
                    {React.cloneElement(item.icon, { className: "h-5 w-5" })}
                  </IconButton>
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop Menu */}
          <div className="hidden xl:flex gap-2 items-center min-w-0 flex-nowrap overflow-x-auto">
            <ul className="flex gap-1 wide:gap-2 flex-nowrap">
              {navItems.map(renderNavItem)}

              {isLoggedIn && (
                <li className="flex items-center justify-center">
                  <MegaMenuWithHover unreadMessages={unreadMessages} canManageCredits={canManageCredits} canUseProspects={canUseProspects} />
                </li>
              )}

              {isFree7DaysTrial && daysRemainingFreeTrial > 0 && (
                <li className="flex items-center justify-center whitespace-nowrap">
                  <Typography
                    as="a"
                    href="/admin/upgrade"
                    className="text-xs text-blue-200 font-medium"
                  >
                    <span className="hidden wide:inline">
                      Free Trial {daysRemainingFreeTrial} day
                      {daysRemainingFreeTrial !== 1 ? "s" : ""} remaining –{" "}
                    </span>
                    <b>Upgrade</b>
                  </Typography>
                </li>
              )}
            </ul>
          </div>

          {/* Right actions (pushed to the far right) */}
          <div className="hidden xl:flex ml-auto items-center gap-2 flex-shrink-0">
            {/* Notification API calls temporarily disabled.
            <NotificationBell
              onMarkRead={(id) =>
                console.log(`Alert ${id} marked as read in StickyOrgNavbar.`)
              }
            />
            */}
            {Number(availableCredits) > 0 && <Link to="/credits" className="flex flex-shrink-0 items-center whitespace-nowrap px-3 py-2 text-sm font-semibold text-white hover:bg-gray-900/10 focus:outline-none focus:ring-2 focus:ring-white/80">{availableCredits} credits</Link>}
            <LogoutButton />
          </div>          

          {/* Mobile menu button */}
          <div className="flex xl:hidden ml-auto items-center gap-1">
            {Number(availableCredits) > 0 && <Link to="/credits" className="flex flex-shrink-0 items-center whitespace-nowrap px-2 py-2 text-xs font-semibold text-white hover:bg-gray-900/10 focus:outline-none focus:ring-2 focus:ring-white/80 sm:px-3 sm:text-sm">{availableCredits} credits</Link>}
            <span className="hidden lg:inline text-sm font-medium">Menu</span>
            <IconButton
              variant="text"
              className="!h-11 !w-11 !max-h-none !max-w-none text-white"
              ref={menuButtonRef}
              aria-label="Toggle menu"
              aria-expanded={openNav}
              aria-controls="org-mobile-menu"
              onClick={() => setOpenNav((isOpen) => !isOpen)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </IconButton>
          </div>
        </div>

        {/* Mobile Menu */}
          <Collapse open={openNav}>
          <ul
            id="org-mobile-menu"
            ref={mobileMenuRef}
            onKeyDown={handleMobileMenuKeyDown}
            className="flex flex-col gap-2"
          >
            <li className="flex justify-end">
              <IconButton
                variant="text"
                className="!h-11 !w-11 !max-h-none !max-w-none text-white"
                aria-label="Close menu"
                onClick={closeMobileMenu}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </IconButton>
            </li>
            {navItems.map((item) => renderNavItem(item, true))}
            {canManageCredits && <><li><Link to="/credit-catalog" onClick={closeMobileMenu} className={GUEST_MOBILE_NAV_LINK_CLASS}>Credit Catalog</Link></li><li><Link to="/promo-campaigns" onClick={closeMobileMenu} className={GUEST_MOBILE_NAV_LINK_CLASS}>Promo Codes</Link></li></>}
            {isLoggedIn && (
              <li className="flex items-center justify-center py-2">
                <MegaMenuWithHover mobile onNavigate={closeMobileMenu} unreadMessages={unreadMessages} canManageCredits={canManageCredits} canUseProspects={canUseProspects} />
              </li>
            )}
            {isFree7DaysTrial && daysRemainingFreeTrial > 0 && (
              <li className="w-full px-2 py-2 text-center text-xs text-blue-200">
                <Typography as="a" href="/admin/upgrade" className="font-medium">
                  Free Trial {daysRemainingFreeTrial} day
                  {daysRemainingFreeTrial !== 1 ? "s" : ""} remaining – <b>Upgrade</b>
                </Typography>
              </li>
            )}
            {/* Notification API calls temporarily disabled.
            <li className="flex items-center justify-center py-2">
              <NotificationBell
                onMarkRead={(id) =>
                  console.log(`Alert ${id} marked as read in mobile menu.`)
                }
              />
            </li>
            */}
            <li>
              <LogoutButton fullWidthOnMobile />
            </li>
          </ul>
        </Collapse>
      </Navbar>
      <BrandGuidelineReminder />
    </div>
  );
}
